import React, { useEffect, useRef, useState } from 'react';
import { useFormik } from 'formik';
import { Button } from 'primereact/button';
import { Message } from 'primereact/message';
import { Toast } from 'primereact/toast';
import { useHistory } from 'react-router-dom';

import './Recipe.css';
import validationSchema from './validationSchema';
import ApiService from '../services/ApiService';
import { useAuth } from '../context/auth';
import useQuery from '../utils/useQuery';
import RecipeMediaSection from './components/RecipeMediaSection.jsx';
import RecipeFormFields from './components/RecipeFormFields.jsx';
import IngredientsSection from './components/IngredientsSection.jsx';
import { getRecipeTypeDefaults } from './recipeTypeDefaults';

const api = new ApiService();

const RecipeCreator = (props) => {
	const { authTokens } = useAuth();
	const { execute } = useQuery();
	const history = useHistory();

	const toast = useRef(null);

	const [loading, setLoading] = useState(false);

	const [allFlavors, setAllFlavors] = useState([]);
	const [charCountDescription, setCharCountDescription] = useState(0);
	const maxCharCountDescription = 255;

	const [percentageSum, setPercentageSum] = useState(0);
	const [areFlavorIdsUnique, setAreFlavorIdsUnique] = useState(true);

	const [imageBlob, setImageBlob] = useState(null);
	const [iconBlob, setIconBlob] = useState(null);

	const [recipeTypeSettings, setRecipeTypeSettings] = useState([]);
	const [categories, setCategories] = useState([]);
	const [cupConfigurations, setCupConfigurations] = useState([]);
	const [cupConfigurationsLoading, setCupConfigurationsLoading] = useState(true);
	const isExistingRecipe = Boolean(props.location.state);
	const isDuplicatedRecipe = Boolean(props.location.state?.duplicatedRecipe);

	useEffect(() => {
		// if initial values are passed in, also update the char count
		if (props.location.state && props.location.state.description) {
			setCharCountDescription(props.location.state.description.length);
		}
	}, [props.location.state]);

	useEffect(() => {
		const headers = {
			'X-Firebase-Auth-Client': authTokens.token,
		};

		// get all flavors
		const getAllFlavors = async () => {
			try {
				const result = await api.getFlavors(headers);
				// loop through flavors and create an array of objects with the key, text, and value properties for the dropdown
				const flavorOptions = result.data.map((flavor) => {
					return {
						key: flavor.id,
						text: flavor.name,
						value: flavor.id,
					};
				});

				setAllFlavors(flavorOptions);
			} catch (error) {
				console.log('error', error);

				toast.current.show({
					severity: 'error',
					summary: 'Error Message',
					detail: 'Error getting flavors',
					life: 3000,
				});
			}
		};

		getAllFlavors();

		// cleanup
		return () => {
			setAllFlavors([]);
		};
	}, []);

	function sanitizeFileName(fileName) {
		return fileName
			.normalize('NFD')
			.replace(/[\u0300-\u036f]/g, '')
			.replace(/[^a-zA-Z0-9._-]/g, '_');
	}

	const getDuplicatedRecipeId = (location) => {
		if (!location) {
			return null;
		}

		const pathname = location.startsWith('http') ? new URL(location).pathname : location;
		const matches = pathname.match(/\/api\/recipes\/([^/?#]+)|\/recipes\/([^/?#]+)/);
		return matches ? matches[1] || matches[2] : null;
	};

	const getCreatedRecipeId = async (response) => {
		const recipeIdFromLocation = getDuplicatedRecipeId(response?.headers?.get('Location'));
		if (recipeIdFromLocation) {
			return recipeIdFromLocation;
		}

		try {
			const responseText = await response.clone().text();
			const recipeIdFromText = getDuplicatedRecipeId(responseText);
			if (recipeIdFromText) {
				return recipeIdFromText;
			}

			const data = JSON.parse(responseText);
			return data?.id || data?.recipeId || data?.recipe?.id || null;
		} catch (error) {
			return null;
		}
	};

	const buildRecipePayload = (values, awsImage, awsIcon) => ({
		name: values.recipeName,
		description: values.description,
		image: awsImage,
		smoothieParts: values.smoothieParts.map((part) => {
			return { flavorId: part.flavorId, percentage: part.percentage };
		}),
		categories: values.category,
		badgeIconUrl: awsIcon,
		waterRatio: values.waterRatio,
		weightRatio: values.weightRatio,
		steamConfiguration: values.steamConfiguration,
		blendConfiguration: values.blendConfiguration,
		recipeType: values.recipeType,
	});

	const buildRecipeState = (recipe, recipeId, duplicatedRecipe = false) => ({
		...(props.location.state || {}),
		id: recipeId,
		name: recipe.name,
		description: recipe.description,
		image: recipe.image,
		smoothieParts: recipe.smoothieParts.map((part) => {
			const currentPart = props.location.state?.smoothieParts?.find(
				(smoothiePart) => smoothiePart.flavor?.id === part.flavorId,
			);

			return {
				flavor: currentPart?.flavor || { id: part.flavorId },
				percentage: part.percentage,
			};
		}),
		categories: recipe.categories,
		badgeIconUrl: recipe.badgeIconUrl,
		waterRatio: recipe.waterRatio,
		weightRatio: recipe.weightRatio,
		steamConfiguration: recipe.steamConfiguration,
		blendConfiguration: recipe.blendConfiguration,
		recipeType: recipe.recipeType,
		duplicatedRecipe,
	});

	const saveRecipe = async (values, duplicateRecipe = false) => {
		setLoading(true);
		let awsImage = values.image;
		let awsIcon = values.icon;
		const baseUrl = api.getBaseUrl();

		try {
			if (imageBlob) {
				awsImage = await uploadImages(imageBlob, values.recipeName);
			}

			if (iconBlob) {
				awsIcon = await uploadImages(iconBlob, values.recipeName);
			}

			const recipe = buildRecipePayload(values, awsImage, awsIcon);
			const shouldUpdateRecipe = isExistingRecipe && !duplicateRecipe;

			if (shouldUpdateRecipe) {
				const updateOptions = {
					method: 'PUT',
					body: JSON.stringify(recipe),
				};
				const response = await execute(`${baseUrl}/api/recipes/${props.location.state.id}`, updateOptions);
				if (!response) {
					throw new Error('Recipe update failed');
				}
			} else {
				const createOptions = {
					method: 'POST',
					body: JSON.stringify(recipe),
				};
				const response = await execute(`${baseUrl}/api/recipes`, createOptions);
				if (!response) {
					throw new Error('Recipe creation failed');
				}

				if (duplicateRecipe) {
					const duplicatedRecipeId = await getCreatedRecipeId(response);
					if (!duplicatedRecipeId) {
						throw new Error('Duplicated recipe id was missing');
					}
					history.push('/recipe/update', buildRecipeState(recipe, duplicatedRecipeId, true));
				}
			}

			toast.current.show({
				severity: 'success',
				summary: duplicateRecipe ? 'Recipe Duplicated' : isExistingRecipe ? 'Recipe Updated' : 'Recipe Created',
				detail: duplicateRecipe
					? 'Recipe has been duplicated'
					: isExistingRecipe
						? 'Recipe has been updated'
						: 'Recipe has been created',
				life: 3000,
			});
		} catch (error) {
			console.log('error', error);

			toast.current.show({
				severity: 'error',
				summary: 'Error Message',
				detail: duplicateRecipe ? 'Error duplicating recipe' : 'Error creating recipe',
				life: 3000,
			});
		} finally {
			setLoading(false);
			setImageBlob(null);
			setIconBlob(null);
		}
	};

	const duplicateRecipe = async () => {
		const errors = await formik.validateForm();

		if (Object.keys(errors).length > 0) {
			formik.setTouched(
				Object.keys(formik.values).reduce((touched, field) => ({ ...touched, [field]: true }), {}),
				false,
			);
			toast.current.show({
				severity: 'error',
				summary: 'Error Message',
				detail: 'Resolve the field errors before duplicating',
				life: 10000,
			});
			return;
		}

		await saveRecipe(formik.values, true);
	};

	const formik = useFormik({
		enableReinitialize: true,
		initialValues: {
			recipeName: props.location?.state?.name || '',
			description: props.location?.state?.description || '',
			category: props.location?.state?.categories || ['ORIGINAL'],
			recipeType: props.location?.state?.recipeType || '',
			smoothieParts: props.location.state?.smoothieParts.map((part) => {
				return { flavorId: part.flavor.id, percentage: part.percentage };
			}) || [{ flavorId: '', percentage: 0 }],
			waterRatio: props.location?.state?.waterRatio ?? null,
			image: props.location?.state?.image || '',
			icon: props.location?.state?.badgeIconUrl || '',
			weightRatio: props.location?.state?.weightRatio ?? null,
			steamConfiguration: props.location?.state?.steamConfiguration ?? '',
			blendConfiguration: props.location?.state?.blendConfiguration ?? '',
		},
		validationSchema: validationSchema,
		onSubmit: async (values) => {
			await saveRecipe(values);
		},
	});

	// Keep description counter in sync with current value
	const description = formik.values.description;
	useEffect(() => {
		setCharCountDescription(description ? description.length : 0);
	}, [description]);

	const uploadImages = async (data, name) => {
		if (data !== undefined) {
			const headers = {
				'X-Firebase-Auth-Client': authTokens.token,
			};

			const sanitizedName = sanitizeFileName(data.name || name); // image name
			const renamedFile = new File([data], sanitizedName, { type: data.type });

			const formData = new FormData();
			formData.append('file', renamedFile);
			formData.append('name', sanitizedName); // image name
			formData.append('type', 'recipes');

			const result = await api.uploadFile(formData, headers);

			if (result.status === 200) {
				return result.data;
			} else {
				toast.current.show({
					severity: 'error',
					summary: 'Error Message',
					detail: 'Error uploading image',
					life: 10000,
				});
			}
		}
	};

	useEffect(() => {
		// Calculate the sum of percentages whenever formik.values.smoothieParts changes
		const sum = formik.values.smoothieParts.reduce((acc, curr) => acc + Number(curr.percentage), 0);
		setPercentageSum(sum);

		// Check if flavorIds are unique
		const flavorIds = formik.values.smoothieParts.map((part) => part.flavorId);
		const uniqueFlavorIds = [...new Set(flavorIds)];
		setAreFlavorIdsUnique(flavorIds.length === uniqueFlavorIds.length);
	}, [formik.values.smoothieParts]);

	useEffect(() => {
		const headers = {
			'X-Firebase-Auth-Client': authTokens.token,
		};

		// Fetch categories from the new endpoint
		const fetchCategories = async () => {
			try {
				const result = await api.getRecipeCategories(headers);
				setCategories(result.data);
			} catch (error) {
				console.log('error', error);

				toast.current.show({
					severity: 'error',
					summary: 'Error Message',
					detail: 'Error getting categories',
					life: 3000,
				});
			}
		};

		fetchCategories();

		// cleanup
		return () => {
			setCategories([]);
		};
	}, [authTokens.token]);

	useEffect(() => {
		const headers = {
			'X-Firebase-Auth-Client': authTokens.token,
		};

		const fetchCupConfigurations = async () => {
			setCupConfigurationsLoading(true);

			try {
				const response = await api.getAllCupConfigurations(headers);
				const responseData = response.data;
				const configurations = Array.isArray(responseData)
					? responseData
					: Array.isArray(responseData?.cupConfigurations)
						? responseData.cupConfigurations
						: Array.isArray(responseData?.content)
							? responseData.content
							: responseData?.id && responseData?.cupSize != null
								? [responseData]
								: [];

				setCupConfigurations(configurations);
			} catch (error) {
				console.log('error', error);
				setCupConfigurations([]);

				toast.current.show({
					severity: 'error',
					summary: 'Error Message',
					detail: 'Error getting cup configurations',
					life: 3000,
				});
			} finally {
				setCupConfigurationsLoading(false);
			}
		};

		fetchCupConfigurations();

		return () => {
			setCupConfigurations([]);
			setCupConfigurationsLoading(false);
		};
	}, [authTokens.token]);

	// Recipe type settings
	useEffect(() => {
		const fetchRecipeTypeSettings = async () => {
			try {
				const headers = {
					'X-Firebase-Auth-Client': authTokens.token,
				};

				const response = await api.getRecipeTypeSettings(headers);
				setRecipeTypeSettings(response.data);

			} catch (error) {
				toast.current.show({
					severity: 'error',
					summary: 'Error fetching recipe type settings',
					detail: 'Could not load recipe type settings',
					life: 3000,
				});
			}
		};

		fetchRecipeTypeSettings();
	}, [authTokens.token]);

	useEffect(() => {

		const selectedType = (formik.values.recipeType || '').toUpperCase();
		const settings = recipeTypeSettings.find((s) => (s.recipeType || '').toUpperCase() === selectedType);
		const defaults = getRecipeTypeDefaults(formik.values, settings, formik.touched.recipeType);

		if (Object.keys(defaults).length > 0) {
			formik.setValues({ ...formik.values, ...defaults }, true);
		}
	}, [formik.values.recipeType, recipeTypeSettings, formik.touched.recipeType]);

	let submitLabel;
	let submitIcon;
	if (loading) {
		submitLabel = 'Loading...';
		submitIcon = 'pi pi-spin pi-spinner';
	} else if (isExistingRecipe) {
		submitLabel = 'Update';
		submitIcon = 'pi pi-check';
	} else {
		submitLabel = 'Create';
		submitIcon = 'pi pi-plus';
	}

	return (
		<div>
			<Toast ref={toast} />

			<form onSubmit={formik.handleSubmit} style={{ maxWidth: '800px', margin: '0 auto' }}>
				<div className="flex align-items-center justify-content-between mb-3 mt-3">
					<h2 className="m-0 text-900" style={{ fontWeight: 600 }}>
						Details
					</h2>
				</div>

				<div className="p-fluid p-formgrid p-grid surface-50 border-1 surface-border border-round p-3 md:p-4">
					{isDuplicatedRecipe && (
						<div className="col-12 mb-3">
							<Message
								severity="info"
								text="You are editing a duplicated recipe. Make any changes you need, then save it with Update."
								className="w-full justify-content-start"
							/>
						</div>
					)}

					<RecipeMediaSection formik={formik} setImageBlob={setImageBlob} setIconBlob={setIconBlob} />

					<RecipeFormFields
						formik={formik}
						categories={categories}
						recipeTypeSettings={recipeTypeSettings}
						charCountDescription={charCountDescription}
						maxCharCountDescription={maxCharCountDescription}
					/>

					<IngredientsSection
						formik={formik}
						allFlavors={allFlavors}
						percentageSum={percentageSum}
						areFlavorIdsUnique={areFlavorIdsUnique}
						cupConfigurations={cupConfigurations}
						cupConfigurationsLoading={cupConfigurationsLoading}
					/>

					<div className="mt-4 flex justify-content-end gap-2">
						{isExistingRecipe && !isDuplicatedRecipe && (
							<Button
								type="button"
								disabled={loading}
								label="Duplicate"
								icon="pi pi-copy"
								className="w-auto p-button-secondary"
								onClick={duplicateRecipe}
							/>
						)}
						<Button
							type="submit"
							disabled={loading}
							label={submitLabel}
							icon={submitIcon}
							className="w-auto"
							onClick={() => {
								if (Object.keys(formik.errors).length > 0) {
									toast.current.show({
										severity: 'error',
										summary: 'Error Message',
										detail: 'Resolve the field errors before submitting',
										life: 10000,
									});
								}
							}}
						/>
					</div>
				</div>
			</form>
		</div>
	);
};

export default RecipeCreator;
